import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import {
  Banner,
  Button,
  ErrorState,
  focusFirstInvalid,
  PageHead,
  SelectField,
  Skeleton,
  TextArea,
  TextField,
  useToast,
} from "@ui/react";
import { ChevronLeftIcon } from "@ui/react/icons";
import {
  getDirectBookingSettings,
  updateDirectBookingSettings,
} from "../../lib/api/contractor";
import { MarketplaceModeNotice, useBookableMode } from "./ModeGate";
import { errorMessage } from "../../lib/errorMessage";

/* The server resolves this with time.LoadLocation, so an invented string is a
 * 400. A short curated list of zones a South African contractor might actually
 * work in beats a free-text box that can only be got wrong. */
const TIMEZONES = [
  // NOT "Africa/Cape_Town" — the IANA database has no such zone, and the
  // server validates with time.LoadLocation, so offering it guaranteed a 400
  // for the very contractors most likely to pick it. All of South Africa is
  // Africa/Johannesburg.
  "Africa/Johannesburg",
  "Africa/Windhoek",
  "Africa/Gaborone",
  "Africa/Maseru",
  "Africa/Mbabane",
  "Africa/Maputo",
  "Africa/Harare",
  // NOT "UTC": every booking/appointment render in this app (lib/format.ts
  // dateTime/dateOnly) is hard-coded to Africa/Johannesburg, so a contractor
  // on any offset other than +02:00 would see their own diary shifted. All
  // seven zones above ARE permanently +02:00 (Windhoek dropped DST in 2017),
  // so they render correctly today; UTC is the one choice that would lie.
];

const DEFAULT_TIMEZONE = "Africa/Johannesburg";

export function BookingPolicyScreen() {
  const bookable = useBookableMode();
  const toast = useToast();
  const formRef = useRef<HTMLFormElement>(null);

  const [state, setState] = useState<{ loading: boolean; error: unknown }>({
    loading: true,
    error: null,
  });
  const [reloadKey, setReloadKey] = useState(0);

  const [timezone, setTimezone] = useState(DEFAULT_TIMEZONE);
  const [leadTimeHours, setLeadTimeHours] = useState("0");
  const [bufferMinutes, setBufferMinutes] = useState("0");
  const [horizonDays, setHorizonDays] = useState("7");
  const [policyText, setPolicyText] = useState("");
  const [freeUntil, setFreeUntil] = useState("0");
  const [partialUntil, setPartialUntil] = useState("0");
  const [partialPercent, setPartialPercent] = useState("0");

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!bookable) return;

    let ignore = false;
    setState({ loading: true, error: null });

    getDirectBookingSettings()
      .then((settings) => {
        if (ignore) return;
        setTimezone(settings.bookingTimezone || DEFAULT_TIMEZONE);
        setLeadTimeHours(String(settings.bookingLeadTimeHours ?? 0));
        setBufferMinutes(String(settings.bookingBufferMinutes ?? 0));
        // Clamp on load: the API maps every response through
        // EffectiveHorizonDays, which can no longer return above 7, but
        // Firebase Hosting and Cloud Run deploy independently, so a freshly
        // deployed site can briefly talk to an API build that predates that
        // clamp. The field below rejects anything above 7 — without this
        // clamp a stale value from an old API would lock the whole form,
        // including fields the contractor never touched.
        setHorizonDays(String(Math.min(settings.bookingHorizonDays || 7, 7)));
        setPolicyText(settings.directBookingPolicyText ?? "");
        setFreeUntil(String(settings.cancellationPolicy?.freeUntilHoursBefore ?? 0));
        setPartialUntil(String(settings.cancellationPolicy?.partialRefundUntilHoursBefore ?? 0));
        setPartialPercent(String(settings.cancellationPolicy?.partialRefundPercent ?? 0));
        setState({ loading: false, error: null });
      })
      .catch((error: unknown) => {
        if (!ignore) setState({ loading: false, error });
      });

    return () => {
      ignore = true;
    };
  }, [bookable, reloadKey]);

  const validate = (): boolean => {
    const next: Record<string, string> = {};

    const whole = (value: string, key: string, label: string, min: number, max?: number, unit = "days") => {
      const parsed = Number(value);
      if (!Number.isInteger(parsed) || parsed < min || (max !== undefined && parsed > max)) {
        next[key] = max !== undefined
          ? `${label} must be between ${min} and ${max} ${unit}.`
          : `${label} must be a whole number of ${min} or more.`;
      }
      return parsed;
    };

    whole(leadTimeHours, "leadTimeHours", "Notice", 0);
    whole(bufferMinutes, "bufferMinutes", "The gap", 0);
    whole(horizonDays, "horizonDays", "How far ahead", 1, 7, "days");
    const free = whole(freeUntil, "freeUntil", "The free-cancellation window", 0);
    const partial = whole(partialUntil, "partialUntil", "The part-refund window", 0);

    const percent = Number(partialPercent);
    if (!Number.isInteger(percent) || percent < 0 || percent > 100) {
      next["partialPercent"] = "Use a whole number between 0 and 100.";
    }

    // Both windows count backwards from the booking, so a part refund that
    // starts further out than the free one can never apply.
    if (
      !next["freeUntil"] &&
      !next["partialUntil"] &&
      partial > 0 &&
      free > 0 &&
      partial >= free
    ) {
      next["partialUntil"] =
        "This must be closer to the booking than the free-cancellation window, or it can never apply.";
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSave = async () => {
    if (!validate()) {
      focusFirstInvalid(formRef.current);
      return;
    }

    setSaving(true);
    setSaveError(null);
    try {
      await updateDirectBookingSettings({
        bookingTimezone: timezone,
        bookingLeadTimeHours: Number(leadTimeHours),
        bookingBufferMinutes: Number(bufferMinutes),
        bookingHorizonDays: Number(horizonDays),
        directBookingPolicyText: policyText.trim(),
        cancellationPolicy: {
          freeUntilHoursBefore: Number(freeUntil),
          partialRefundUntilHoursBefore: Number(partialUntil),
          partialRefundPercent: Number(partialPercent),
        },
      });
      toast.show("Booking rules saved.");
    } catch (error) {
      setSaveError(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  if (!bookable) {
    return <MarketplaceModeNotice what="Booking rules" />;
  }

  // A zone already on the account that isn't in the curated list still belongs
  // in the picker, otherwise opening this screen would quietly change it.
  const zoneOptions = TIMEZONES.includes(timezone) ? TIMEZONES : [timezone, ...TIMEZONES];

  return (
    <div className="stack stack--loose">
      {/* No eyebrow: the back link below names this same section, and a head
          that says "Booking setup" twice in adjacent lines is stammering. See
          PageHead for the rule. */}
      <PageHead
        title="Booking rules"
        back={
          <Link className="back-link" to="/settings/booking">
            <ChevronLeftIcon />
            Booking setup
          </Link>
        }
      />

      {state.loading ? (
        <div role="status">
          <span className="sr-only">Loading your booking rules</span>
          <Skeleton height="24rem" />
        </div>
      ) : state.error ? (
        <ErrorState message={errorMessage(state.error)} onRetry={() => setReloadKey((key) => key + 1)} />
      ) : (
        <>
          {saveError ? (
            <Banner tone="danger" title="We couldn't save your rules">
              {saveError}
            </Banner>
          ) : null}

          <form
            ref={formRef}
            className="form"
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              void onSave();
            }}
          >
            <div className="card card--dense">
              <div className="stack">
                <h2 className="headline-sm">When people can book you</h2>

                <SelectField
                  label="Booking timezone"
                  value={timezone}
                  hint="Your weekly hours and every booking time are read in this zone."
                  onChange={(event) => setTimezone(event.target.value)}
                >
                  {zoneOptions.map((zone) => (
                    <option key={zone} value={zone}>
                      {zone.replace(/_/g, " ")}
                    </option>
                  ))}
                </SelectField>

                <div className="field-grid">
                  <TextField
                    label="Notice needed (hours)"
                    type="number"
                    min={0}
                    step={1}
                    value={leadTimeHours}
                    error={errors["leadTimeHours"] ?? null}
                    hint="How far in advance a customer must book. 0 lets them book the next free slot."
                    onChange={(event) => setLeadTimeHours(event.target.value)}
                  />
                  <TextField
                    label="Gap between jobs (minutes)"
                    type="number"
                    min={0}
                    step={5}
                    value={bufferMinutes}
                    error={errors["bufferMinutes"] ?? null}
                    hint="Travel and pack-up time held clear either side of a booking."
                    onChange={(event) => setBufferMinutes(event.target.value)}
                  />
                  <TextField
                    label="How far ahead (days)"
                    type="number"
                    min={1}
                    max={7}
                    step={1}
                    value={horizonDays}
                    error={errors["horizonDays"] ?? null}
                    hint="The furthest date a customer can pick — at most 7 days, because the card hold that pays you expires after 7."
                    onChange={(event) => setHorizonDays(event.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="card card--dense">
              <div className="stack">
                <h2 className="headline-sm">If a customer cancels</h2>
                <p className="muted">
                  These windows count backwards from the booking's start time. Cancelling
                  inside both of them means no refund.
                </p>

                <div className="field-grid">
                  <TextField
                    label="Full refund until (hours before)"
                    type="number"
                    min={0}
                    step={1}
                    value={freeUntil}
                    error={errors["freeUntil"] ?? null}
                    hint="Cancel earlier than this and the customer gets everything back."
                    onChange={(event) => setFreeUntil(event.target.value)}
                  />
                  <TextField
                    label="Part refund until (hours before)"
                    type="number"
                    min={0}
                    step={1}
                    value={partialUntil}
                    error={errors["partialUntil"] ?? null}
                    hint="Between this and the full-refund window, they get the share below."
                    onChange={(event) => setPartialUntil(event.target.value)}
                  />
                  <TextField
                    label="Part refund (%)"
                    type="number"
                    min={0}
                    max={100}
                    step={5}
                    value={partialPercent}
                    error={errors["partialPercent"] ?? null}
                    hint="0 to 100."
                    onChange={(event) => setPartialPercent(event.target.value)}
                  />
                </div>

                <Banner tone="warn" title="This is the customer's policy, not yours">
                  If you cancel a booking the customer has already paid for, they are refunded
                  in full regardless of these settings.
                </Banner>
              </div>
            </div>

            <div className="card card--dense">
              <div className="stack">
                <h2 className="headline-sm">What customers are told</h2>
                <TextArea
                  label="Your booking policy"
                  value={policyText}
                  hint="Shown to customers before they confirm. Parking, access, what you need on site, anything they should know."
                  onChange={(event) => setPolicyText(event.target.value)}
                />
              </div>
            </div>

            <div className="cluster">
              <Button type="submit" loading={saving}>
                Save booking rules
              </Button>
            </div>
          </form>
        </>
      )}
    </div>
  );
}
